import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    TextField,
    Typography,
    Chip,
    Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import dayjs from "dayjs";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { CommonPatientDetails, CommonGridToolBarWithFilterText } from "../../../Components/Common Components/CommonComponents";
import { Colors } from "../../../Styles/Colors";
import Loader from "../../../Components/Loader";
import SchedulerView from "./SchedulerView";

const AyurvedaScheduler = () => {
    const [filterDate, setFilterDate] = useState(dayjs());
    const [admissionType, setAdmissionType] = useState('Walkin'); // Walkin | IP
    const [schedulerData, setSchedulerData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [showSchedulerView, setShowSchedulerView] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [printLoading, setPrintLoading] = useState(false);

    // Fetch scheduler list
    const getSchedulerList = useCallback(async () => {
        try {
            setIsLoading(true);
            setSchedulerData([]);
            const dateStr = filterDate.format("YYYY-MM-DD");
            const response = await RestAPIService.getAll(
                `${Serviceurls.AYURVEDA_SCHEDULER_VIEW}?date=${dateStr}&admission_type=${admissionType}`
            );
            if (response?.data?.status === "success" && response.data.data && response.data.data.length > 0) {
                setSchedulerData(response.data.data);
            }


        } catch (error) {
            setErrorMsg(error.response.data.message);
            setSchedulerData([]);
        } finally {
            setIsLoading(false);
        }
    }, [filterDate, admissionType]);

    useEffect(() => {
        getSchedulerList();
    }, [getSchedulerList]);

    // Get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "completed":
                return { bg: "#3db236", border: "#008919", text: "#ffffff" };
            case "scheduled":
                return { bg: "#E8F5E9", border: "#4CAF50", text: "#2E7D32" };
            case "ongoing":
                return { bg: "#E3F2FD", border: "#2196F3", text: "#1565C0" };
            case "canceled":
            case "cancelled":
                return { bg: "#FFEBEE", border: "#F44336", text: "#C62828" };
            case "not scheduled":
                return { bg: "#FFEBEE", border: "#F44336", text: "#C62828" };
            default:
                return { bg: "#E8F5E9", border: "#4CAF50", text: "#2E7D32" };
        }
    };

    // Handle navigate to grid view
    const handleNavigateToGrid = (schedule) => {
        setSelectedSchedule(schedule);
        setShowSchedulerView(true);
    };

    // Handle back from grid view
    const handleBackFromGrid = () => {
        setShowSchedulerView(false);
        setSelectedSchedule(null);
        getSchedulerList(); // Refresh list
    };

    const handleTablePrint = () => {
        const dateStr = filterDate.format("YYYY-MM-DD");
        setPrintLoading(true);
        RestAPIService.getAll(`${Serviceurls.AYURVEDA_SCHEDULER_VIEW}?date=${dateStr}&admission_type=${admissionType}&export_type=pdf`)
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                window.open(URL.createObjectURL(file));
            })
            .catch((e) => setErrorMsg(e?.response?.data?.message || e?.message || "Print failed"))
            .finally(() => setPrintLoading(false));
    };

    const handleJobCardPrint = (schedule) => {
        setPrintLoading(true);
        RestAPIService.getAll(`${Serviceurls.AYURVEDA_JOB_CARD_PRINT}?id=${schedule?.walkin_appointment_id}`)
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                window.open(URL.createObjectURL(file));
            })
            .catch((e) => setErrorMsg(e?.response?.data?.message || e?.message || "Print failed"))
            .finally(() => setPrintLoading(false));
    }

    // If showing SchedulerView, render it instead
    if (showSchedulerView) {
        return (
            <SchedulerView
                initialSchedule={selectedSchedule}
                onBack={handleBackFromGrid}
                treatmentDate={filterDate.format("YYYY-MM-DD")}
                patientDetails={selectedSchedule}
                therapyDetails={selectedSchedule}
                duration={selectedSchedule?.duration}
                instructions={selectedSchedule?.instruction}
                no_of_therapist={selectedSchedule?.treatments?.length ? Math.max(...selectedSchedule?.treatments?.map(t => t.no_of_therapist || 0)) : 1}
                treatment_ms_id={selectedSchedule?.treatment_ms_id}
                session={selectedSchedule?.session}
            />
        );
    }

    const columns = [
        {
            field: "patient_name",
            headerName: "Patient Details",
            flex: 0.22,
            minWidth: 140,
            renderCell: (params) => <CommonPatientDetails data={params.row} />,
        },
        {
            field: "doctor_name",
            headerName: "Doctor Name",
            flex: 0.15,
            minWidth: 120,
            renderCell: (params) => params.row.doctor_name || "-",
        },
        {
            field: "treatment_name",
            headerName: "Therapy Name",
            flex: 0.15,
            minWidth: 120,
            renderCell: (params) => (
                <Tooltip title={params.row.treatment_name || "-"}>
                    <Typography variant="body2" sx={{ maxWidth: 200, fontSize: "0.9vw" }}>
                        {params.row.treatment_name?.length > 15 ? params.row.treatment_name?.slice(0, 15) + "..." : params.row.treatment_name || "-"}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: "therapist_name",
            headerName: "Therapist Name",
            flex: 0.15,
            minWidth: 120,
            renderCell: (params) => {
                const therapistNames = params.row?.therapists?.map((t) => t.therapist_name) || [];
                const therapistText = therapistNames.join(", ");
                return (
                    <Tooltip title={therapistText || "-"}>
                        <Typography variant="body2" sx={{ maxWidth: 200, fontSize: "0.9vw" }}>
                            {therapistText?.length > 15 ? therapistText?.slice(0, 15) + "..." : therapistText || "-"}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: "room_name",
            headerName: "Therapy Room",
            flex: 0.12,
            minWidth: 100,
            renderCell: (params) => params.row.room_name || "-",
        },
        {
            field: "session",
            headerName: "Session",
            flex: 0.08,
            minWidth: 80,
            renderCell: (params) => params.row.session || params.row.session_count || "-",
        },
        {
            field: "treatment_time",
            headerName: "Start Time",
            flex: 0.1,
            minWidth: 80,
            renderCell: (params) => params.row.start_time || params.row.treatment_time || "-",
        },
        {
            field: "end_time",
            headerName: "End Time",
            flex: 0.1,
            minWidth: 80,
            renderCell: (params) => params.row.end_time || "-",
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.08,
            minWidth: 120,
            renderCell: (params) => (
                <Chip
                    label={params.row.status || "Pending"}
                    size="small"
                    sx={{
                        backgroundColor: getStatusColor(params.row.status).bg,
                        color: getStatusColor(params.row.status).text,
                        border: `1px solid ${getStatusColor(params.row.status).border}`,
                    }}
                />
            ),
        },
        {
            field: "instruction",
            headerName: "Instructions",
            flex: 0.15,
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ maxWidth: 200, fontSize: "0.9vw" }}>
                    {params.row.instruction.length > 15 ? <Tooltip title={params.row.instruction} arrow> <Typography>{params.row.instruction.slice(0, 15) + "..."}</Typography></Tooltip> : params.row.instruction || "-"}
                </Typography>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 0.08,
            minWidth: 80,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
                    <IconButton
                        size="small"
                        disabled={params.row.status === "Not Scheduled"}
                        onClick={() => handleJobCardPrint(params.row)}
                        sx={{
                            backgroundColor: params.row.status === "Not Scheduled" ? "#4da3d4" : "#E0E0E0",
                            color: params.row.status === "Not Scheduled" ? "#fff" : "#000",
                            "&:hover": { backgroundColor: params.row.status === "Not Scheduled" ? "#3a8bb3" : "#E0E0E0" },
                        }}
                    >
                        <PrintIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                        disabled={params.row.status !== "Not Scheduled"}
                        size="small"
                        onClick={() => handleNavigateToGrid(params.row)}
                        sx={{
                            backgroundColor: params.row.status === "Not Scheduled" ? "#4da3d4" : "#E0E0E0",
                            color: "#fff",
                            "&:hover": { backgroundColor: params.row.status === "Not Scheduled" ? "#3a8bb3" : "#E0E0E0" },
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 2, bgcolor: Colors.Background }}>
            <Box className="eMed_OP_AptHead" sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight={500}>
                    Scheduler
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ButtonGroup
                        size="small"
                        disableElevation
                        sx={{
                            "& .MuiButton-root": { textTransform: "capitalize", minWidth: 72, px: 1.5, borderColor: "divider" },
                            "& .MuiButtonGroup-grouped:not(:last-of-type)": { borderColor: "divider" },
                        }}
                    >
                        <Button
                            variant={admissionType === "Walkin" ? "contained" : "outlined"}
                            onClick={() => setAdmissionType("Walkin")}
                            sx={{ fontWeight: admissionType === "Walkin" ? 600 : 400 }}
                        >
                            Walk-in
                        </Button>
                        <Button
                            variant={admissionType === "IP" ? "contained" : "outlined"}
                            onClick={() => setAdmissionType("IP")}
                            sx={{ fontWeight: admissionType === "IP" ? 600 : 400 }}
                        >
                            IP
                        </Button>
                    </ButtonGroup>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DatePicker
                            label="Select Date"
                            value={filterDate}
                            onChange={(newValue) => setFilterDate(newValue || dayjs())}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={{ width: 200 }} />
                            )}
                        />
                    </LocalizationProvider>
                    <Tooltip title="Print" placement="top" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleTablePrint}
                                disabled={!schedulerData?.length || printLoading}
                            >
                                <PrintIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {isLoading ? (
                <Loader />
            ) : (
                <Box sx={{ height: "calc(100vh - 200px)", minHeight: "64vh", marginTop: "0.5vw" }}>
                    <DataGrid
                        rows={schedulerData.map((row, index) => ({ ...row, sno: row.id ?? index + 1 }))}
                        columns={columns}
                        getRowId={(row) => row.sno}
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={(model) => {
                            setPage(model.page);
                            setPageSize(model.pageSize);
                        }}
                        pageSizeOptions={[10, 20, 30, 50]}
                        pagination
                        paginationMode="client"
                        localeText={{
                            toolbarColumns: "",
                            toolbarDensity: "",
                            toolbarExport: "",
                            toolbarFilters: "",
                            toolbarExportPrint: "",
                        }}
                        components={{
                            Toolbar: () => (
                                <Box sx={{ p: 0.5 }}>
                                    <CommonGridToolBarWithFilterText />
                                </Box>
                            ),
                            NoRowsOverlay: () => (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                    No schedules found
                                </Box>
                            ),
                        }}
                        headerHeight={80}
                        rowHeight={80}
                        density="compact"
                        disableSelectionOnClick
                        loading={isLoading}
                        sx={{
                            border: "none",
                            "& .MuiDataGrid-columnHeaders": { backgroundColor: Colors.TableHeaderBarBGColor || "#f5f5f5" },
                        }}
                    />
                </Box>
            )
            }

            {
                errorMsg && (
                    <ToastMsg severity="error" msg={errorMsg} msgPop={() => setErrorMsg("")} />
                )
            }
            {
                successMsg && (
                    <ToastMsg severity="success" msg={successMsg} msgPop={() => setSuccessMsg("")} />
                )
            }
        </Box >
    );
};

export default AyurvedaScheduler;
